import { PartnerInfos, PartnerDecennale, Project } from "types";
import { useState } from "react";
import Icon1 from "@assets/icons/Icon";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { AddPartnerToProjectModal } from "@components/modals/AddPartnerToProjectModal";
import { ModifyPartnerModal } from "@components/modals/ModifyPartnerModal";
import { AddDecennaleModal } from "@components/modals/AddDecennaleModal";
import { PDFViewerModal } from "@components/modals/PDFViewerModal";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import { COMPANY_TYPE_OPTIONS, partnerMatchesType } from "@constants/partnerTypes";
import {
  syncDecennalesFolder,
  computePlanningEndIso,
  pickRelevantDecennales,
  decennalesCoverPlanning,
} from "@helpers/decennalesHelpers";
import { showNotification } from "context/notification.service";

export const PartnersSection = ({
  project,
  setProject,
  readOnly = false,
}: {
  project: Project;
  setProject?: (project: Project) => void;
  readOnly?: boolean;
}) => {
  const [addPartnerForSection, setAddPartnerForSection] = useState<string | false>(false);
  const [modifyPartnerId, setModifyPartnerId] = useState<string | null>(null);
  const [decennalePartner, setDecennalePartner] = useState<{ id: string; name: string } | null>(null);
  const [pdfViewerUrl, setPdfViewerUrl] = useState<string | null>(null);
  const storePartners = useProjectStore((state) => state.partners);
  const libraryDocuments = useProjectStore((state) => state.libraryDocuments);
  const plannings = useProjectStore((state) => state.plannings);
  const orgData = useDataStore((state) => state.orgData);

  const activePlanning = plannings.find((p) => p.project_id === project.id);
  const planningEndIso = computePlanningEndIso(activePlanning);

  // Re-sync the Décennales folder after partners change. Surfaces warnings
  // for partners without coverage so the user sees them immediately.
  const applyPartnerUpdate = (updatedProject: Project) => {
    if (!setProject) return;
    const { folders, warnings } = syncDecennalesFolder(
      updatedProject,
      storePartners,
      planningEndIso,
    );
    const next: Project = { ...updatedProject, folders };
    setProject(next);
    warnings.forEach((w) => showNotification({ type: "error", message: w }));
  };

  // Get the project creation date as ISO string for the MBMO/org-level
  // décennale check (org décennales must cover the project date).
  const projectDateObj = project.infos.general.creation_date?.toDate
    ? project.infos.general.creation_date.toDate()
    : new Date((project.infos.general.creation_date as any)?.seconds * 1000);
  const projectDateIso = projectDateObj.toISOString().slice(0, 10);

  // For artisan rows: surface every décennale we'd attach to the folder so
  // the user sees (and can open) all of them, and can still add more.
  const getPartnerRelevantDecennales = (partnerId: string): PartnerDecennale[] => {
    const storePartner = storePartners.find((p) => p.id === partnerId);
    return pickRelevantDecennales(storePartner?.decennales || []);
  };
  const partnerCoversPlanning = (partnerId: string): boolean => {
    if (!planningEndIso) return true;
    const storePartner = storePartners.find((p) => p.id === partnerId);
    return decennalesCoverPlanning(storePartner?.decennales || [], planningEndIso);
  };

  const modifyPartner = modifyPartnerId
    ? storePartners.find((p) => p.id === modifyPartnerId)
    : null;

  return (
    <div className="flex flex-col gap-1 w-full max-w-500">
      {modifyPartner && (
        <ModifyPartnerModal
          initialPartner={modifyPartner}
          onClose={() => setModifyPartnerId(null)}
        />
      )}
      {decennalePartner && (
        <AddDecennaleModal
          partnerId={decennalePartner.id}
          partnerName={decennalePartner.name}
          projectId={project.id}
          onClose={() => setDecennalePartner(null)}
        />
      )}
      {pdfViewerUrl && (
        <PDFViewerModal
          pdfUrl={pdfViewerUrl}
          title="Décennale"
          onClose={() => setPdfViewerUrl(null)}
        />
      )}
      {addPartnerForSection !== false && (
        <AddPartnerToProjectModal
          onClose={() => setAddPartnerForSection(false)}
          filterByType={addPartnerForSection || undefined}
          onSelect={(partner) => {
            if (!setProject) return;
            const sectionKey = addPartnerForSection;
            const existing = project.infos.partners.find((p) => p.id === partner.id);
            if (existing) {
              if (sectionKey && !existing.company_types?.includes(sectionKey)) {
                const updated = project.infos.partners.map((p) =>
                  p.id === partner.id
                    ? { ...p, company_types: [...(p.company_types || []), sectionKey] }
                    : p,
                );
                applyPartnerUpdate({ ...project, infos: { ...project.infos, partners: updated } });
              }
            } else {
              const partnerWithSection = sectionKey && !partner.company_types?.includes(sectionKey)
                ? { ...partner, company_types: [...(partner.company_types || []), sectionKey] }
                : partner;
              applyPartnerUpdate({
                ...project,
                infos: { ...project.infos, partners: [...project.infos.partners, partnerWithSection] },
              });
            }
            setAddPartnerForSection(false);
          }}
          existingPartnerIds={addPartnerForSection
            ? project.infos.partners
                .filter((p) => p.company_types?.includes(addPartnerForSection))
                .map((p) => p.id)
            : []}
        />
      )}

      {/* Header row */}
      <div className="flex flex-row items-center justify-between px-4 pb-1">
        <div className="flex-1" />
        <div className="text-h4 text-neutral-400 text-right" style={{ minWidth: 180 }}>
          Décennales
        </div>
      </div>

      {/* Maître d'Oeuvre — MBMO (visual, always shown) */}
      {(() => {
        const orgDecennales = orgData?.config?.decennales || [];
        const validOrgDecennales = orgDecennales
          .filter((d) => projectDateIso >= d.start_date && projectDateIso <= d.end_date)
          .map((d) => {
            const libDoc = libraryDocuments.find((ld) => ld.id === d.library_document_id);
            return { ...d, url: libDoc?.file?.url };
          });
        return (
          <div
            className="flex flex-row items-center justify-between px-4 py-2.5 rounded-lg border transition-colors bg-neutral-50 border-neutral-300"
          >
            <div className="flex flex-row items-center gap-2 flex-1">
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-black" />
              <div className="flex flex-col">
                <div className="text-h3 font-medium text-black">
                  MAÎTRE D'OEUVRE
                </div>
                <div className="text-h4 text-neutral-400">
                  {orgData?.infos?.name || "MBMO"}
                </div>
              </div>
            </div>
            <div className="flex flex-row items-center gap-2">
              {validOrgDecennales.length > 0 ? (
                validOrgDecennales.map((d, i) => (
                  <button
                    key={i}
                    onClick={() => d.url && setPdfViewerUrl(d.url)}
                    className={`flex items-center gap-1 text-neutral-500 ${d.url ? "hover:text-neutral-700 cursor-pointer" : ""}`}
                    title="Décennale"
                  >
                    <Icon1 name="pdf" size={22} />
                  </button>
                ))
              ) : !readOnly ? (
                <div className="text-h4 text-red-400 flex items-center gap-1">
                  <Icon1 name="warning" size={18} color="red" />
                  <span>Décennale manquante</span>
                </div>
              ) : null}
            </div>
          </div>
        );
      })()}

      {/* Partner rows by type — iterate COMPANY_TYPE_OPTIONS in declared
          order so the sections always appear in the business-defined
          sequence (Terrassement → Gros œuvre → … → Autre). */}
      {COMPANY_TYPE_OPTIONS
        .map((type) => {
          const candidates = project.infos.partners
            .filter((p) => partnerMatchesType(p.company_types, type.value))
            .sort((a, b) => (a.company_types?.length || 0) - (b.company_types?.length || 0));
          // Hide types with no assigned partner
          if (candidates.length === 0) return null;
          return (
            <div key={type.value} className="flex flex-col gap-1">
              {candidates.map((assigned) => (
                <div
                  key={`${type.value}-${assigned.id}`}
                  className="flex flex-row items-center justify-between px-4 py-2.5 rounded-lg border transition-colors bg-white border-neutral-200"
                >
                  <div className="flex flex-row items-center gap-2 flex-1">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-neutral-800" />
                    <div className="flex flex-col">
                      <div className="text-h3 font-medium text-black">
                        {type.label.toUpperCase()}
                      </div>
                      <button
                        className={`text-h4 text-neutral-400 text-left ${!readOnly ? "hover:text-neutral-600 hover:underline cursor-pointer" : ""}`}
                        onClick={() => !readOnly && setModifyPartnerId(assigned.id)}
                      >
                        {assigned.company_name || assigned.display_name}
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-row items-center gap-2">
                    {!readOnly && (
                      <SecondaryButton
                        danger={true}
                        variant="small"
                        onClick={() => {
                          if (!setProject) return;
                          const updated = project.infos.partners
                            .map((p) => {
                              if (p.id !== assigned.id) return p;
                              const remaining = (p.company_types || []).filter((t) => t !== type.value);
                              return remaining.length > 0 ? { ...p, company_types: remaining } : null;
                            })
                            .filter(Boolean) as PartnerInfos[];
                          applyPartnerUpdate({ ...project, infos: { ...project.infos, partners: updated } });
                        }}
                      >
                        Retirer
                      </SecondaryButton>
                    )}
                    {assigned.id !== "maitre_ouvrage" && (() => {
                      const relevant = getPartnerRelevantDecennales(assigned.id);
                      const covers = partnerCoversPlanning(assigned.id);
                      const addButton = !readOnly ? (
                        <SecondaryButton
                          danger={relevant.length === 0 || !covers}
                          variant="small"
                          onClick={() =>
                            setDecennalePartner({
                              id: assigned.id,
                              name: assigned.company_name || assigned.display_name,
                            })
                          }
                        >
                          {relevant.length === 0 ? "Ajouter une décennale" : "+"}
                        </SecondaryButton>
                      ) : null;

                      if (relevant.length === 0) {
                        return addButton;
                      }

                      return (
                        <>
                          {relevant.map((d, i) => (
                            <button
                              key={i}
                              onClick={() => d.url && setPdfViewerUrl(d.url)}
                              className="flex items-center gap-1 text-neutral-500 hover:text-neutral-700 cursor-pointer"
                              title={
                                d.start_date && d.end_date
                                  ? `Décennale ${d.start_date} → ${d.end_date}`
                                  : "Décennale"
                              }
                            >
                              <Icon1 name="pdf" size={22} />
                            </button>
                          ))}
                          {addButton}
                        </>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
          );
        })}

      {!readOnly && (
        <SecondaryButton
          startIcon={<Icon1 name="plus" size={16} />}
          label="Ajouter un intervenant"
          variant="large"
          onClick={() => setAddPartnerForSection("")}
        />
      )}
    </div>
  );
};
